/* eslint-disable react/prop-types */
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAuthorization from '../../app/hooks/useAuthorization'
import * as builder from '../ducks/builder'
import { useGetFeatureFlagsQuery } from '../services/featureFlags'
import { displayPage, resetDefaultPermission } from '../utils/utils'
import { LayoutSplashScreen } from './LayoutContext'

/**
 * Used to synchronize current layout `menuConfig`, `layoutConfig` and
 * `htmlClassService` with redux store.
 */
export default function LayoutInitializer({ children, menuConfig, layoutConfig, htmlClassService }) {
  const dispatch = useDispatch()
  const builderState = useSelector(({ builder }) => builder) // eslint-disable-line no-shadow
  const { data: featureFlagsData = {} } = useGetFeatureFlagsQuery()
  const permissions = useSelector(({ userPermissions }) => userPermissions.data.permissions)

  const hasValidM365LicensingReportSubscription = useSelector(
    ({ meta }) => meta.hasValidM365LicensingReportSubscription,
  )
  const hasValidPostureBaselineSubscription = useSelector(({ meta }) => meta.hasValidPostureBaselineSubscription)
  const hasMfaOnlySubscription = useSelector(({ meta }) => meta.hasMfaOnlySubscription)

  const { isAuthorizedForDiscover, isAuthorizedForGroups } = useAuthorization()

  useEffect(() => {
    if (Object.keys(featureFlagsData).length > 0 && menuConfig) {
      const isAugmenttAiEnabled = featureFlagsData['augmentt-ai-assistant']
      const isComplianceAuditEnabled = featureFlagsData['secure-compliance-audit']

      // reset default permission
      resetDefaultPermission(menuConfig)

      try {
        const secureMenuItem = menuConfig.aside.items.find((item) => item.page === 'secure') // Secure Menu

        for (let i = 0; i < secureMenuItem.submenu.length; i += 1) {
          if (hasMfaOnlySubscription) {
            if (secureMenuItem.submenu[i].page === 'secure/security-posture-menu') {
              secureMenuItem.submenu[i].preview = false
              const postureTemplateItem = secureMenuItem.submenu[i].submenu.find(
                (item) => item.page === 'secure/posture-templates',
              )
              postureTemplateItem.preview = true
            } else {
              secureMenuItem.submenu[i].preview = true
            }
          }

          // If posture baseline subscription not set, set as preview only
          if (!hasValidPostureBaselineSubscription && secureMenuItem.submenu[i].page === 'secure/posture-templates') {
            secureMenuItem.submenu[i].preview = true
          }

          if (secureMenuItem.submenu[i].page === 'secure/security-posture-menu') {
            const complianceAuditItem = secureMenuItem.submenu[i].submenu.find(
              (item) => item.page === 'secure/compliance-audit',
            )

            if (complianceAuditItem) {
              complianceAuditItem.visible = isComplianceAuditEnabled
            }
          }

          if (
            !hasValidPostureBaselineSubscription &&
            (secureMenuItem.submenu[i].page === 'secure/policy-templates' ||
              secureMenuItem.submenu[i].page === 'secure/policy-management' ||
              secureMenuItem.submenu[i].page === 'secure/policy-management/policies' ||
              secureMenuItem.submenu[i].page === 'secure/policy-management/named-locations' ||
              secureMenuItem.submenu[i].page === 'secure/defender' ||
              secureMenuItem.submenu[i].page === 'secure/defender/policies' ||
              secureMenuItem.submenu[i].page === 'secure/defender/templates' ||
              secureMenuItem.submenu[i].page === 'secure/entra-id')
          ) {
            // When licensing is not valid the parent menu item and submenu will be greyed out
            // and the submenu will be disabled. Set preview on the parent menu item to style as grey
            // without disabling to keep the submenu expandable, while the submenu items are disabled.
            secureMenuItem.submenu[i].preview = true

            // eslint-disable-next-line no-plusplus
            for (let x = 0; x < secureMenuItem.submenu[i].submenu.length; x++) {
              secureMenuItem.submenu[i].submenu[x].disabled = true
            }
          }

          if (secureMenuItem.submenu[i].page === 'secure/entra-id') {
            const groupsSubmenu = secureMenuItem.submenu[i]

            if (!isAuthorizedForGroups && hasValidPostureBaselineSubscription) {
              groupsSubmenu.visible = false
            }
          }
        }
      } catch (e) {
        console.error('ERROR, secure menu error', e)
      }

      try {
        const reportsMenuItem = menuConfig.aside.items.find((item) => item.page === 'reports') // reports menu

        if (hasMfaOnlySubscription) {
          const mfaOnlyAllowedPages = ['reports/threats', 'reports/mfa', 'reports/licensing', 'scheduled-reports']
          reportsMenuItem?.submenu?.forEach((item) => {
            if (!mfaOnlyAllowedPages.includes(item.page)) {
              item.preview = true // eslint-disable-line no-param-reassign
            }
          })
        }
      } catch (e) {
        console.error('ERROR, reports/licensing menu error', e)
      }

      try {
        // Development page should only appear while FF enabled
        const configMenuItem = menuConfig.aside.items.find((item) => item.page === 'system-configuration')

        if (configMenuItem && configMenuItem?.submenu?.length > 0) {
          const developmentPage = configMenuItem?.submenu?.find((sub) => sub.page === 'development')

          if (developmentPage) {
            if (isAugmenttAiEnabled) {
              developmentPage.visible = true
            } else {
              developmentPage.visible = false
            }
          }
        }
      } catch (e) {
        console.error('Error', e)
      }

      try {
        // Discover - menu item visible or not
        const discoverMenuItem = menuConfig.aside.items.find((item) => item.page === 'discovery') // Discover menu

        if (!isAuthorizedForDiscover) {
          discoverMenuItem.visible = false
        }

        if (hasMfaOnlySubscription) {
          discoverMenuItem.preview = true
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < discoverMenuItem.submenu.length; i++) {
            discoverMenuItem.submenu[i].preview = true
          }
        }
      } catch (e) {
        console.error('ERROR, Discover-report menu error', e)
      }

      try {
        // Engage
        const engageMenuItem = menuConfig.aside.items.find((item) => item.page === 'engage')
        if (hasMfaOnlySubscription) {
          engageMenuItem.preview = true
        }
      } catch (e) {
        console.error('ERROR, Engage menu error', e)
      }

      // Update menu config
      dispatch(builder.actions.setMenuConfig(menuConfig))
    }

    if (permissions && menuConfig) {
      // biome-ignore lint/complexity/noForEach: <explanation>
      menuConfig.aside.items.forEach((menuItem) => {
        if (!displayPage(`/${menuItem.page}`, permissions)) {
          menuItem.visible = false // eslint-disable-line no-param-reassign
        } else if (menuItem.submenu) {
          // biome-ignore lint/complexity/noForEach: <explanation>
          menuItem.submenu.forEach((submenuItem) => {
            if (!displayPage(`/${submenuItem.page}`, permissions)) {
              submenuItem.visible = false // eslint-disable-line no-param-reassign
            } else if (submenuItem.submenu) {
              // biome-ignore lint/complexity/noForEach: <explanation>
              submenuItem.submenu.forEach((submenuItem2) => {
                if (!displayPage(`/${submenuItem2.page}`, permissions)) {
                  submenuItem2.visible = false // eslint-disable-line no-param-reassign
                } else if (submenuItem2.submenu) {
                  submenuItem2.submenu.forEach((submenuItem3) => {
                    if (!displayPage(`/${submenuItem3.page}`, permissions)) {
                      submenuItem3.visible = false // eslint-disable-line no-param-reassign
                    }
                  })
                }
              })
            }
          })

          // if all submenus under a menu are hidden, hide the parent menu
          if (menuItem.submenu.every((submenuItem) => submenuItem.visible !== true)) {
            menuItem.visible = false // eslint-disable-line no-param-reassign
          }
        }
      })

      // hide configuration parent menu if no child found
      const configMenu = menuConfig.aside.items.find((x) => x.page === 'system-configuration')
      if (configMenu) {
        let configMenuChildrenVisible = false
        // eslint-disable-next-line array-callback-return
        configMenu.submenu.map((sub) => {
          if (sub.visible) {
            configMenuChildrenVisible = true
          }
        })
        configMenu.visible = configMenuChildrenVisible
      }
    }
  }, [
    menuConfig,
    featureFlagsData,
    permissions,
    dispatch,
    hasValidM365LicensingReportSubscription,
    hasValidPostureBaselineSubscription,
    hasMfaOnlySubscription,
    isAuthorizedForDiscover,
    isAuthorizedForGroups,
  ])

  useEffect(() => {
    if (layoutConfig.demo !== builderState.layoutConfig.demo) {
      dispatch(builder.actions.setLayoutConfigs(layoutConfig))
    }
  }, [dispatch, builderState, layoutConfig])

  useEffect(() => {
    dispatch(builder.actions.setHtmlClassService(htmlClassService))
  }, [dispatch, htmlClassService])

  return htmlClassService === builderState.htmlClassServiceObjects ? (
    // Render content when `htmlClassService` synchronized with redux store.
    children
  ) : (
    // Otherwise sow loading splash screen.
    <LayoutSplashScreen />
  )
}
